import React, { FC, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Badge,
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ChevronRightRounded,
  ExpandMoreRounded,
  OpenInNewRounded,
} from "@mui/icons-material";
import color from "../../Constants/colors";
import { NavItem, isNavItemActive } from "./navigationItems";

export const itemSx = (active: boolean, collapsed: boolean) => ({
  minHeight: 42,
  borderRadius: "10px",
  px: collapsed ? 0 : 1.25,
  justifyContent: collapsed ? "center" : "flex-start",
  color: color.primary_dark,
  backgroundColor: active ? color.white : "transparent",
  boxShadow: active ? "0 1px 3px rgba(16, 24, 40, 0.08)" : "none",
  "&:hover": {
    backgroundColor: active ? color.white : "rgba(3, 46, 77, 0.06)",
  },
});

const iconSx = (collapsed: boolean) => ({
  minWidth: collapsed ? 0 : 36,
  color: "inherit",
  "& .MuiSvgIcon-root": { fontSize: 22 },
});

const labelProps = (active: boolean) => ({
  noWrap: true,
  sx: {
    fontSize: 14,
    lineHeight: "20px",
    fontWeight: active ? 700 : 500,
    color: color.primary_dark,
  },
});

const badgeSx = () => ({
  "& .MuiBadge-badge": {
    backgroundColor: color.accent,
    color: color.white,
    fontSize: 10,
    fontWeight: 700,
    height: 16,
    minWidth: 16,
    px: 0.5,
  },
});

export const DashboardMenuLabel: FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    sx={{
      px: 1.25,
      pb: 1,
      fontSize: 12,
      fontWeight: 500,
      color: color.gray500,
    }}
  >
    {children}
  </Typography>
);

export const sidebarMenuPaperSx = () => ({
  backgroundColor: color.white,
  border: `1px solid ${color.gray200}`,
  borderRadius: "12px",
  boxShadow: "0 8px 24px rgba(16, 24, 40, 0.12)",
  maxHeight: "none",
  "& .MuiMenuItem-root": {
    color: color.primary_dark,
    borderRadius: "8px",
    mx: 0.75,
    "&:hover": { backgroundColor: color.gray100 },
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: color.gray100,
      color: color.primary_dark,
    },
  },
});

interface ItemProps {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}

export const DashboardMenuItem: FC<ItemProps> = ({
  item,
  collapsed,
  onNavigate,
}) => {
  const { pathname } = useLocation();
  const active = isNavItemActive(item, pathname);

  const handleClick = () => {
    item.onClick?.();
    onNavigate?.();
  };

  const content = (
    <>
      <ListItemIcon sx={iconSx(collapsed)}>
        <Badge badgeContent={collapsed ? item.badge : null} sx={badgeSx()}>
          {item.icon}
        </Badge>
      </ListItemIcon>
      {!collapsed && (
        <>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={labelProps(active)}
          />
          {item.badge && (
            <Box
              component="span"
              sx={{
                ml: 1,
                px: 0.75,
                minWidth: 20,
                height: 20,
                borderRadius: "10px",
                backgroundColor: color.accent,
                color: color.white,
                fontSize: 11,
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {item.badge}
            </Box>
          )}
          {item.externalUrl && (
            <OpenInNewRounded
              sx={{ ml: 1, fontSize: 16, color: color.gray400 }}
            />
          )}
          {item.drillIn && (
            <ChevronRightRounded
              sx={{ ml: 1, fontSize: 20, color: color.gray500 }}
            />
          )}
        </>
      )}
    </>
  );

  const commonProps = {
    onClick: handleClick,
    "aria-label": collapsed ? item.label : undefined,
    sx: itemSx(active, collapsed),
  };

  const button = item.drillIn ? (
    <ListItemButton
      {...commonProps}
      onClick={item.drillIn}
      aria-current={active ? "page" : undefined}
    >
      {content}
    </ListItemButton>
  ) : item.path ? (
    <ListItemButton
      component={Link}
      to={item.path}
      aria-current={active ? "page" : undefined}
      {...commonProps}
    >
      {content}
    </ListItemButton>
  ) : item.externalUrl ? (
    <ListItemButton
      component="a"
      href={item.externalUrl}
      target="_blank"
      rel="noopener noreferrer"
      {...commonProps}
    >
      {content}
    </ListItemButton>
  ) : (
    <ListItemButton {...commonProps}>{content}</ListItemButton>
  );

  if (!collapsed) return button;

  return (
    <Tooltip title={item.label} placement="right" arrow>
      {button}
    </Tooltip>
  );
};

export const DashboardMenuGroup: FC<ItemProps> = ({
  item,
  collapsed,
  onNavigate,
}) => {
  const { pathname } = useLocation();
  const active = isNavItemActive(item, pathname);
  const [open, setOpen] = useState(active);
  const [flyoutAnchor, setFlyoutAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  const children = item.children ?? [];

  if (collapsed) {
    const closeFlyout = () => setFlyoutAnchor(null);

    return (
      <>
        <Tooltip title={flyoutAnchor ? "" : item.label} placement="right" arrow>
          <ListItemButton
            onClick={(event) => setFlyoutAnchor(event.currentTarget)}
            aria-label={item.label}
            aria-haspopup="menu"
            aria-expanded={Boolean(flyoutAnchor)}
            sx={itemSx(active, true)}
          >
            <ListItemIcon sx={iconSx(true)}>{item.icon}</ListItemIcon>
          </ListItemButton>
        </Tooltip>
        <Menu
          anchorEl={flyoutAnchor}
          open={Boolean(flyoutAnchor)}
          onClose={closeFlyout}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: { ...sidebarMenuPaperSx(), ml: 1.5, minWidth: 220 },
            },
          }}
        >
          <Typography
            sx={{
              px: 2,
              pt: 0.5,
              pb: 1,
              fontSize: 12,
              fontWeight: 600,
              color: color.gray500,
            }}
          >
            {item.label}
          </Typography>
          {children.map((child) => {
            const childActive = isNavItemActive(child, pathname);
            return (
              <MenuItem
                key={child.key}
                component={Link}
                to={child.path ?? ""}
                selected={childActive}
                onClick={() => {
                  closeFlyout();
                  onNavigate?.();
                }}
                sx={{ gap: 1.5, py: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    color: color.primary_dark,
                    "& .MuiSvgIcon-root": { fontSize: 20 },
                  }}
                >
                  {child.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: childActive ? 700 : 500,
                    color: color.primary_dark,
                  }}
                >
                  {child.label}
                </Typography>
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  }

  return (
    <>
      <ListItemButton
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        sx={itemSx(false, false)}
      >
        <ListItemIcon sx={iconSx(false)}>{item.icon}</ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={labelProps(active && !open)}
        />
        <ExpandMoreRounded
          sx={{
            fontSize: 20,
            color: color.gray500,
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List
          disablePadding
          sx={{
            ml: 2.5,
            pl: 1,
            borderLeft: `1px solid ${color.gray300}`,
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
            py: 0.25,
          }}
        >
          {children.map((child) => (
            <DashboardMenuItem
              key={child.key}
              item={child}
              collapsed={false}
              onNavigate={onNavigate}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

interface MenuProps {
  items: NavItem[];
  collapsed: boolean;
  onNavigate?: () => void;
}

export const DashboardMenu: FC<MenuProps> = ({
  items,
  collapsed,
  onNavigate,
}) => {
  return (
    <List
      disablePadding
      sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
    >
      {items.map((item) =>
        item.children?.length ? (
          <DashboardMenuGroup
            key={item.key}
            item={item}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ) : (
          <DashboardMenuItem
            key={item.key}
            item={item}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ),
      )}
    </List>
  );
};
